import { useCallback, useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import CardEditor from './components/layout/CardEditor';
import CardPreview from './components/layout/CardPreview';
import FieldModal from './components/modals/FieldModal';
import { INITIAL_CARD_SECTIONS } from './constants/initialSections';
import { useCardSections } from './hooks/useCardSections';
import { useModal } from './hooks/useModal';
import PlaceholderLoader from './PlaceholderLoader/PlaceholderLoader';
import './PropertyCardLayoutTab.css';

import { AREA_TO_SECTION_ID } from './constants/areaMappings';

const PropertyCardLayoutTab = ({ onActionsReady, isSaving, isResetting, showToast, setIsLoading }) => {
  const { sections, addField, updateField, deleteField, moveFieldUp, moveFieldDown, setSections } = useCardSections(INITIAL_CARD_SECTIONS);
  const { activeModal, editingField, openModal, closeModal } = useModal();
  const [isLocalLoading, setIsLocalLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const lastToastRef = useRef({ id: null, time: 0 });

  const getApiSettings = () => {
    const havenlyticsData = window.HavenlyticsData || {};
    return {
      nonce: havenlyticsData.hvnlyNabApi?.nonce || '',
      cardBuilderUrl: havenlyticsData.hvnlyNabApi?.url ? `${havenlyticsData.hvnlyNabApi.url}/pb-card-builder` : '/wp-json/hvnlynab/v1/pb-card-builder'
    };
  };

  useEffect(() => {
    if (onActionsReady) {
      onActionsReady({
        saveConfiguration: handleSaveConfiguration,
        resetConfiguration: handleResetConfiguration
      });
    }
  }, [sections]);

  useEffect(() => {
    if (isSaving) {
      handleSaveConfiguration();
    }
  }, [isSaving]);

  useEffect(() => {
    if (isResetting) {
      handleResetConfiguration();
    }
  }, [isResetting]);

  const fetchSections = useCallback(async () => {
    try {
      setIsLocalLoading(true);
      if (setIsLoading) setIsLoading(true);
      
      const apiSettings = getApiSettings();

      const response = await fetch(apiSettings.cardBuilderUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': apiSettings.nonce
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(__('Permission denied. Please check if you have administrator privileges.', 'havenlytics'));
        }
        throw new Error(__('HTTP error! status:', 'havenlytics') + ` ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.data && data.data.length > 0) {
        setSections(data.data);
      } else {
        setSections(INITIAL_CARD_SECTIONS);
      }
    } catch (error) {
      setSections(INITIAL_CARD_SECTIONS);
      
      if (showToast) {
        showToast('error', __('Load Failed', 'havenlytics'), error.message || __('Failed to load card layout', 'havenlytics'));
      }
    } finally {
      setIsLocalLoading(false);
      if (setIsLoading) setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, [setSections, setIsLoading, showToast]);

  const saveSections = useCallback(async (sectionsToSave) => {
    const saveKey = `save-${Date.now()}`;
    const now = Date.now();
    
    if (lastToastRef.current.id === saveKey && now - lastToastRef.current.time < 2000) {
      return false;
    }
    
    lastToastRef.current = {
      id: saveKey,
      time: now
    };

    try {
      if (setIsLoading) setIsLoading(true);
      
      const apiSettings = getApiSettings();

      const response = await fetch(apiSettings.cardBuilderUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': apiSettings.nonce
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          sections: sectionsToSave.map(section => ({
            id: section.id,
            title: section.title,
            icon: section.icon,
            mode: section.mode || 'both',
            fields: section.fields.map(field => ({
              id: field.id,
              type: field.type,
              label: field.label,
              mode: field.mode || 'both',
              value: field.value,
              order: field.order || 0
            })),
            order: section.order || 0
          }))
        })
      });

      if (!response.ok) {
        throw new Error(__('HTTP error! status:', 'havenlytics') + ` ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        if (showToast) {
          showToast('success', __('Configuration Saved', 'havenlytics'), data.message || __('Property card layout saved successfully!', 'havenlytics'));
        }
        return true;
      } else {
        throw new Error(data.message || __('Failed to save sections', 'havenlytics'));
      }
    } catch (error) {
      if (showToast) {
        showToast('error', __('Save Failed', 'havenlytics'), error.message || __('Failed to save property card layout', 'havenlytics'));
      }
      throw error;
    } finally {
      if (setIsLoading) setIsLoading(false);
    }
  }, [showToast, setIsLoading]);

  const deleteSections = useCallback(async () => {
    const resetKey = `reset-${Date.now()}`;
    const now = Date.now();
    
    if (lastToastRef.current.id === resetKey && now - lastToastRef.current.time < 2000) {
      return false;
    }
    
    lastToastRef.current = {
      id: resetKey,
      time: now
    };

    try {
      if (setIsLoading) setIsLoading(true);
      
      const apiSettings = getApiSettings();

      const response = await fetch(apiSettings.cardBuilderUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': apiSettings.nonce
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(__('HTTP error! status:', 'havenlytics') + ` ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        if (showToast) {
          showToast('success', __('Configuration Reset', 'havenlytics'), data.message || __('Property card layout reset successfully!', 'havenlytics'));
        }
        return true;
      } else {
        throw new Error(data.message || __('Failed to delete sections', 'havenlytics'));
      }
    } catch (error) {
      if (showToast) {
        showToast('error', __('Reset Failed', 'havenlytics'), error.message || __('Failed to reset property card layout', 'havenlytics'));
      }
      throw error;
    } finally {
      if (setIsLoading) setIsLoading(false);
    }
  }, [showToast, setIsLoading]);

  const handleSaveConfiguration = useCallback(async () => {
    try {
      const success = await saveSections(sections);
      return success;
    } catch (error) {
      return false;
    }
  }, [sections, saveSections]);

  const handleResetConfiguration = useCallback(async () => {
    try {
      const success = await deleteSections();
      if (success) {
        setSections(INITIAL_CARD_SECTIONS);
      }
      return success;
    } catch (error) {
      return false;
    }
  }, [deleteSections, setSections]);

  const handleOpenModal = useCallback((area, event) => {
    openModal(area);
  }, [openModal]);

  const handleEditField = useCallback((field, event) => {
    event.stopPropagation();
    
    let area = null;
    for (const [areaKey, sectionId] of Object.entries(AREA_TO_SECTION_ID)) {
      if (sections.some(section => 
        section.id === sectionId && section.fields.some(f => f.id === field.id)
      )) {
        area = areaKey;
        break;
      }
    }
    
    if (area) {
      openModal(area, field);
    }
  }, [sections, openModal]);

  const handleDeleteField = useCallback((fieldId, event) => {
    event.stopPropagation();
    deleteField(fieldId);
    
    if (showToast) {
      showToast('success', __('Field Removed', 'havenlytics'), __('Field deleted successfully', 'havenlytics'));
    }
  }, [deleteField, showToast]);

  const handleMoveFieldUp = useCallback((fieldId, event) => {
    event.stopPropagation();
    moveFieldUp(fieldId);
  }, [moveFieldUp]);

  const handleMoveFieldDown = useCallback((fieldId, event) => {
    event.stopPropagation();
    moveFieldDown(fieldId);
  }, [moveFieldDown]);

  const handleFieldSelect = useCallback((option) => {
    if (editingField) {
      updateField(editingField.id, { 
        type: option.id,
        label: option.title,
        value: option.defaultValue || {},
        mode: option.mode || 'both'
      });
      
      if (showToast) {
        showToast('success', __('Field Updated', 'havenlytics'), `${option.title} ${__('added', 'havenlytics')}`);
      }
      
      closeModal();
    } else {
      addField(activeModal, option.id);
      
      if (showToast) {
        showToast('success', __('Field Added', 'havenlytics'), `${option.title} ${__('added to', 'havenlytics')} ${activeModal}`);
      }
    }
  }, [activeModal, editingField, addField, updateField, closeModal, showToast]);

  useEffect(() => {
    const handleResize = () => {
      if (activeModal) {
        closeModal();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [activeModal, closeModal]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  if (isLocalLoading && isInitialLoad) {
    return <PlaceholderLoader />;
  }

  return (
    <div className="hvnly--builder--int_api__tab_content_panel">
      <div className="hvnly--builder---api_property__builder__main-container">
        <CardEditor 
          sections={sections}
          onOpenModal={handleOpenModal}
          onEditField={handleEditField}
          onDeleteField={handleDeleteField}
          onMoveFieldUp={handleMoveFieldUp}
          onMoveFieldDown={handleMoveFieldDown}
        />
        
        <CardPreview 
          sections={sections}
          variant="enhanced"
        />
        
        <FieldModal 
          isOpen={!!activeModal}
          area={activeModal}
          editingField={editingField}
          onClose={closeModal}
          onFieldSelect={handleFieldSelect}
          cardSections={sections}
        />
      </div>
    </div>
  );
};

export default PropertyCardLayoutTab;